// ─────────────────────────────────────────────────────────────────────────
// Classroom ↔ department / programme, for the classrooms page:
//   useStructure        departments + programmes, loaded once
//   StructurePicker     department select + programme select (filtered)
//   MoveClassroomDialog change one classroom's department / programme
//   BulkAssignDialog    many classrooms at once — the selected rows, or all
//                       classrooms carrying an old free-text programme
//                       (optionally turning that text into a real programme)
// All calls go through Departments/structureApi. When the server doesn't
// have the structure routes yet, useStructure reports available=false and
// the page keeps today's behaviour.
// ─────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Loader2, MoveRight, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useInstituteLabels } from "@/lib/instituteLabels";
import { SearchableSelect } from "@/components/SearchableSelect";
import {
    type ClassroomLinks, type DepartmentProfile, type Programme,
    bulkAssignClassrooms, createProgramme, findClassroomsByProgrammeText,
    listDepartments, listProgrammes, updateClassroomLinks, withLabels,
} from "../Departments/structureApi";

// ── Data ─────────────────────────────────────────────────────────────────
export type Structure = {
    available: boolean | null;          // null = loading, false = server without the routes
    departments: DepartmentProfile[];
    programmes: Programme[];
    reload: () => Promise<void>;
    departmentLabel: (key: string | null | undefined) => string;
    programmeLabel: (id: string | null | undefined) => string;
};

const shownDepartment = (d: DepartmentProfile) =>
    d.displayName === d.departmentName ? d.departmentName.replace(/_/g, " ") : d.displayName;

export function useStructure(): Structure {
    const [ available, setAvailable ] = useState<boolean | null>(null);
    const [ departments, setDepartments ] = useState<DepartmentProfile[]>([]);
    const [ programmes, setProgrammes ] = useState<Programme[]>([]);

    const reload = useCallback(async () => {
        try {
            const [ d, p ] = await Promise.all([ listDepartments(), listProgrammes() ]);
            setDepartments(d);
            setProgrammes(p);
            setAvailable(true);
        } catch {
            setAvailable(false);
        }
    }, []);

    useEffect(() => { reload(); }, [ reload ]);

    return useMemo(() => {
        const deptBy = new Map(departments.map(d => [ d.departmentName, d ]));
        const progBy = new Map(programmes.map(p => [ p.id, p ]));
        return {
            available, departments, programmes, reload,
            departmentLabel: (key) => {
                if (!key) return "";
                const d = deptBy.get(key);
                return d ? shownDepartment(d) : key.replace(/_/g, " ");
            },
            programmeLabel: (id) => (id ? progBy.get(id)?.name || "" : ""),
        };
    }, [ available, departments, programmes, reload ]);
}

// ── Picker ───────────────────────────────────────────────────────────────
type PickerProps = {
    structure: Structure;
    departmentName: string | null;
    programmeId: string | null;
    onChange: (next: { departmentName: string | null; programmeId: string | null }) => void;
    requireDepartment?: boolean;
};

export function StructurePicker({ structure, departmentName, programmeId, onChange, requireDepartment }: PickerProps) {
    const L = useInstituteLabels();
    const inDepartment = structure.programmes.filter(p => p.departmentName === departmentName);

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
                <Label className="text-[11px] font-bold text-muted-foreground uppercase tracking-tight">
                    {L.department} {requireDepartment && <span className="text-red-500">*</span>}
                </Label>
                <SearchableSelect
                    value={departmentName}
                    nullLabel={requireDepartment ? undefined : "— Not assigned —"}
                    placeholder="Choose…"
                    searchPlaceholder={`Search ${L.departments.toLowerCase()}…`}
                    emptyText={`No ${L.departments.toLowerCase()} found.`}
                    options={structure.departments.map(d => ({ value: d.departmentName, label: shownDepartment(d) }))}
                    onChange={(v) => onChange({ departmentName: v, programmeId: null })}
                />
            </div>
            <div className="space-y-1.5">
                <Label className="text-[11px] font-bold text-muted-foreground uppercase tracking-tight">{L.programme}</Label>
                <SearchableSelect
                    value={programmeId}
                    disabled={!departmentName}
                    nullLabel="— None —"
                    searchPlaceholder={`Search ${L.programmes.toLowerCase()}…`}
                    emptyText={`No ${L.programmes.toLowerCase()} found.`}
                    options={inDepartment.map(p => ({ value: p.id, label: p.name, hint: p.singleClassroom ? `Single ${L.classroom.toLowerCase()}` : undefined }))}
                    onChange={(v) => onChange({ departmentName, programmeId: v })}
                />
            </div>
        </div>
    );
}

// The change between a classroom's current links and a picked pair, as the
// server wants it (only what changed; clearing the department clears both).
function linkChange(from: { departmentName: string | null; programmeId: string | null },
    to: { departmentName: string | null; programmeId: string | null }): ClassroomLinks | null {
    if (to.programmeId) return to.programmeId === from.programmeId ? null : { programmeId: to.programmeId };
    if (!to.departmentName) return from.departmentName ? { departmentName: null } : null;
    const links: ClassroomLinks = {};
    if (from.programmeId) links.programmeId = null;
    if (to.departmentName !== from.departmentName) links.departmentName = to.departmentName;
    return Object.keys(links).length ? links : null;
}

// ── Move one classroom ───────────────────────────────────────────────────
type MoveTarget = { id: string; name: string; departmentName: string | null; programmeId: string | null };

export function MoveClassroomDialog({ classroom, structure, onClose, onSaved }: {
    classroom: MoveTarget | null; structure: Structure; onClose: () => void; onSaved: () => void;
}) {
    const L = useInstituteLabels();
    const [ pick, setPick ] = useState<{ departmentName: string | null; programmeId: string | null }>({ departmentName: null, programmeId: null });
    const [ saving, setSaving ] = useState(false);
    const [ error, setError ] = useState<string | null>(null);

    useEffect(() => {
        if (classroom) { setPick({ departmentName: classroom.departmentName, programmeId: classroom.programmeId }); setError(null); }
    }, [ classroom ]);

    if (!classroom) return null;

    const handleSave = async () => {
        const links = linkChange({ departmentName: classroom.departmentName, programmeId: classroom.programmeId }, pick);
        if (!links) { onClose(); return; }
        setSaving(true); setError(null);
        try {
            await withLabels(L, () => updateClassroomLinks(classroom.id, links));
            onSaved();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><MoveRight className="h-5 w-5 text-primary" /> Move {classroom.name}</DialogTitle>
                </DialogHeader>
                {error && <ErrorBox text={error} />}
                <StructurePicker structure={structure} departmentName={pick.departmentName} programmeId={pick.programmeId} onChange={setPick} />
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving…</> : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ── Bulk assign ──────────────────────────────────────────────────────────
type BulkResult = { updated: string[]; failed: { id: string; reason: string }[] };

export function BulkAssignDialog({ open, selectedIds, structure, onClose, onDone }: {
    open: boolean; selectedIds: string[]; structure: Structure; onClose: () => void; onDone: () => void;
}) {
    const L = useInstituteLabels();
    const [ tab, setTab ] = useState<"selected" | "text">(selectedIds.length ? "selected" : "text");
    const [ pick, setPick ] = useState<{ departmentName: string | null; programmeId: string | null }>({ departmentName: null, programmeId: null });
    const [ busy, setBusy ] = useState(false);
    const [ error, setError ] = useState<string | null>(null);
    const [ result, setResult ] = useState<BulkResult | null>(null);

    // "by old programme text"
    const [ oldText, setOldText ] = useState("");
    const [ found, setFound ] = useState<{ id: string; name: string }[] | null>(null);
    const [ makeProgramme, setMakeProgramme ] = useState(true);

    useEffect(() => {
        if (!open) return;
        setTab(selectedIds.length ? "selected" : "text");
        setPick({ departmentName: null, programmeId: null }); setError(null); setResult(null);
        setOldText(""); setFound(null); setMakeProgramme(true);
    }, [ open ]);

    if (!open) return null;

    const targetIds = tab === "selected" ? selectedIds : (found || []).map(c => c.id);

    const handleFind = async () => {
        setBusy(true); setError(null); setFound(null); setResult(null);
        try {
            setFound(await withLabels(L, () => findClassroomsByProgrammeText(oldText.trim())));
        } catch (err: any) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const canApply = targetIds.length > 0 && !!pick.departmentName && !busy;

    const handleApply = async () => {
        setBusy(true); setError(null); setResult(null);
        try {
            let programmeId = pick.programmeId;
            // Turn the old text into a real programme in the chosen department.
            if (tab === "text" && makeProgramme && !programmeId) {
                const created = await withLabels(L, () => createProgramme({
                    name: oldText.trim(), departmentName: pick.departmentName as string, singleClassroom: false,
                }));
                programmeId = created.programme.id;
                await structure.reload();
            }
            const links: ClassroomLinks = programmeId ? { programmeId } : { departmentName: pick.departmentName, programmeId: null };
            const res = await withLabels(L, () => bulkAssignClassrooms(targetIds, links));
            setResult(res);
            onDone();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><MoveRight className="h-5 w-5 text-primary" /> Assign {L.classrooms.toLowerCase()}</DialogTitle>
                </DialogHeader>

                {error && <ErrorBox text={error} />}
                {result && (
                    <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm space-y-1">
                        <p>{result.updated.length} assigned{result.failed.length ? `, ${result.failed.length} not` : ""}.</p>
                        {result.failed.map(f => <p key={f.id} className="text-xs text-red-700">{f.id}: {f.reason}</p>)}
                    </div>
                )}

                <Tabs value={tab} onValueChange={(v) => { setTab(v as "selected" | "text"); setResult(null); }}>
                    <TabsList>
                        <TabsTrigger value="selected" disabled={!selectedIds.length}>Selected ({selectedIds.length})</TabsTrigger>
                        <TabsTrigger value="text">By old {L.programme.toLowerCase()} text</TabsTrigger>
                    </TabsList>
                    <TabsContent value="selected" className="pt-2 text-sm text-muted-foreground">
                        {selectedIds.length} {L.classroom.toLowerCase()}(s) ticked in the list.
                    </TabsContent>
                    <TabsContent value="text" className="pt-2 space-y-2">
                        <p className="text-xs text-muted-foreground">
                            {L.classrooms} made before {L.departments.toLowerCase()} existed carry the {L.programme.toLowerCase()} as typed text.
                            Find them by that exact text.
                        </p>
                        <div className="flex gap-2">
                            <Input placeholder="e.g. BCA" value={oldText} onChange={(e) => setOldText(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && oldText.trim() && handleFind()} />
                            <Button variant="outline" onClick={handleFind} disabled={busy || !oldText.trim()} className="gap-1.5">
                                <Search className="h-4 w-4" /> Find
                            </Button>
                        </div>
                        {found && (
                            <p className="text-xs text-muted-foreground">
                                {found.length
                                    ? `${found.length} found: ${found.slice(0, 6).map(c => c.name).join(", ")}${found.length > 6 ? "…" : ""}`
                                    : `No ${L.classrooms.toLowerCase()} with that text.`}
                            </p>
                        )}
                        {found && found.length > 0 && !pick.programmeId && (
                            <label className="flex items-center justify-between gap-3 rounded border p-2 text-sm">
                                <span>Create a {L.programme.toLowerCase()} named “{oldText.trim()}” for them</span>
                                <Switch checked={makeProgramme} onCheckedChange={setMakeProgramme} />
                            </label>
                        )}
                    </TabsContent>
                </Tabs>

                <StructurePicker structure={structure} departmentName={pick.departmentName} programmeId={pick.programmeId} onChange={setPick} requireDepartment />

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>Close</Button>
                    <Button onClick={handleApply} disabled={!canApply}>
                        {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Working…</> : `Assign ${targetIds.length || ""}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ErrorBox({ text }: { text: string }) {
    return (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" /> {text}
        </div>
    );
}
